/** One opaque hint subscription per clinic/session; data always uses the API. */
import { onValue, ref } from 'firebase/database'
import { z } from 'zod'
import { createApiClient } from './api/client'
import { getEnvironment } from '../lib/environment'
import { getFirebaseServices } from './firebase'
import {
  acceptVisibilityScope,
  subscribeVisibility,
  visibilityEpoch,
} from './visibility-cache'

type Listener = (healthy: boolean) => void
/** One opaque counter per surface; a view wakes only for what it shows. */
export type HintSurface =
  'conversations' | 'aggregates' | 'overview' | 'operational' | 'ownership'
const SURFACES: readonly HintSurface[] = [
  'conversations',
  'aggregates',
  'overview',
  'operational',
  'ownership',
]
type Changed = Set<HintSurface> | 'all'
const hubs = new Map<
  string,
  {
    add: (
      listener: Listener,
      surfaces: readonly HintSurface[] | undefined
    ) => () => void
  }
>()

/** Which surfaces moved between two channel values. Anything not a known
 * per-surface record (first value, a legacy single counter, a surface this
 * build does not know) wakes every view: a hint is never dropped. */
export function changedSurfaces(previous: unknown, next: unknown): Changed {
  const record = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
  if (previous === undefined || !record(previous) || !record(next)) return 'all'
  const changed = new Set<HintSurface>()
  for (const key of new Set([...Object.keys(previous), ...Object.keys(next)])) {
    if (previous[key] === next[key]) continue
    if (!(SURFACES as readonly string[]).includes(key)) return 'all'
    changed.add(key as HintSurface)
  }
  return changed
}

export function subscribeInvalidation(
  tenant: string,
  listener: Listener,
  surfaces?: readonly HintSurface[]
) {
  let user: ReturnType<typeof getFirebaseServices>['auth']['currentUser']
  try {
    user = getFirebaseServices().auth.currentUser
  } catch {
    // No hint channel: callers keep their unhinted polling cadence.
    return () => undefined
  }
  if (!user) return () => undefined
  const key = JSON.stringify([user.uid, tenant])
  let hub = hubs.get(key)
  if (!hub) {
    const listeners = new Map<Listener, readonly HintSurface[] | undefined>()
    let closed = false
    let healthy = false
    let generation = 0
    let stop: (() => void) | undefined
    let retry: ReturnType<typeof setTimeout> | undefined
    let batch: ReturnType<typeof setTimeout> | undefined
    let pending: Changed = new Set()
    let last: unknown
    const active = () =>
      document.visibilityState !== 'hidden' && navigator.onLine !== false
    const emit = (changed: Changed) => {
      if (pending !== 'all')
        if (changed === 'all') pending = 'all'
        else
          changed.forEach((surface) =>
            (pending as Set<HintSurface>).add(surface)
          )
      if (batch) return
      batch = setTimeout(() => {
        batch = undefined
        const woken = pending
        pending = new Set()
        if (!closed && active())
          listeners.forEach((wanted, callback) => {
            if (
              woken === 'all' ||
              wanted === undefined ||
              wanted.some((surface) => woken.has(surface))
            )
              callback(healthy)
          })
      }, 1000)
    }
    const connect = async () => {
      const attempt = ++generation
      stop?.()
      stop = undefined
      clearTimeout(retry)
      healthy = false
      // A (re)attached listener's first value reconciles everything missed.
      last = undefined
      if (
        closed ||
        !active() ||
        getFirebaseServices().auth.currentUser?.uid !== user.uid
      )
        return
      const epoch = visibilityEpoch()
      try {
        const api = createApiClient({
          baseUrl: getEnvironment().userManagementBaseUrl,
          getUser: () => getFirebaseServices().auth.currentUser,
        })
        const scope = await api.request(
          `/dashboard/clinics/${encodeURIComponent(tenant)}/invalidation`,
          {
            responseSchema: z
              .object({ path: z.string(), visibilityScope: z.string() })
              .strict(),
          }
        )
        if (closed || attempt !== generation || epoch !== visibilityEpoch())
          return
        acceptVisibilityScope(scope.visibilityScope)
        const channel = scope.visibilityScope.endsWith(':admin')
          ? 'admin'
          : 'restricted'
        if (scope.path !== `dashboardReadInvalidations/v1/${tenant}/${channel}`)
          throw new Error('Invalid hint scope')
        const database = getFirebaseServices().database
        let synced = false
        let transport = true
        const stopHint = onValue(
          ref(database, scope.path),
          (snapshot) => {
            if (closed || attempt !== generation) return
            const value: unknown = snapshot.val()
            const changed = changedSurfaces(last, value)
            last = value
            synced = true
            healthy = transport
            emit(changed) // Initial snapshot/reconnect closes any read/subscribe race.
          },
          () => {
            if (closed || attempt !== generation) return
            healthy = false
            clearTimeout(retry)
            emit('all')
            retry = setTimeout(() => {
              void connect()
            }, 30000)
          }
        )
        // The SDK reconnects silently and never cancels the hint listener, so
        // its transport state decides health: while it is down every view
        // keeps its unhinted cadence, and on return every view reconciles.
        const stopTransport = onValue(
          ref(database, '.info/connected'),
          (snapshot) => {
            if (closed || attempt !== generation) return
            const connected = snapshot.val() === true
            if (!synced || connected === transport) {
              transport = connected
              return
            }
            transport = connected
            healthy = connected
            emit('all')
          }
        )
        stop = () => {
          stopHint()
          stopTransport()
        }
      } catch {
        if (closed || attempt !== generation) return
        healthy = false
        emit('all')
        retry = setTimeout(() => {
          void connect()
        }, 30000)
      }
    }
    const resume = () => {
      void connect()
    }
    const visibilityStop = subscribeVisibility(resume)
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('online', resume)
    window.addEventListener('offline', resume)
    hub = {
      add(callback, wanted) {
        listeners.set(callback, wanted)
        if (listeners.size === 1) void connect()
        else callback(healthy)
        return () => {
          listeners.delete(callback)
          if (listeners.size) return
          closed = true
          generation += 1
          stop?.()
          clearTimeout(retry)
          clearTimeout(batch)
          visibilityStop()
          document.removeEventListener('visibilitychange', resume)
          window.removeEventListener('online', resume)
          window.removeEventListener('offline', resume)
          hubs.delete(key)
        }
      },
    }
    hubs.set(key, hub)
  }
  return hub.add(listener, surfaces)
}
